// Tumbling E: a 5 x 5 grid of whole-pixel strokes aligned to the device-pixel grid, so it is
// drawn exactly, with no anti-aliasing to alter its contrast. Also draws a centre patch
// for photometer calibration, and (optionally) visualTools' chromatic edge jitter.
//
// Edge jitter (visualTools render_window.py, pipelineChromJitter): pixels within a band
// around the E's outline — `innerPx` inside the strokes, `outerPx` outside — are grouped
// into square blobs of `blobPx`; every `updateFrames` frames each blob is coloured with
// probability `density`, in one of two colours chosen at random, mixed over the letter or
// background. The mixed colours arrive as codes (the runner mixes in linear light and
// encodes through the station's LUT). visualTools finds the outline by an 8-direction pixel
// search; here the distance to the outline is computed exactly from the E's rectangles.

import type { Orientation } from "../../lib/protocol";
import { GLSL_COMMON, uniforms, type DrawContext, type GLStage, type Stimulus } from "../glStage";

type RGB = [number, number, number];

export interface EdgeJitterFrame {
  innerPx: number;
  outerPx: number;
  blobPx: number;
  density: number;
  updateFrames: number;
  /** Codes of background / letter mixed toward jitter colour 1 / 2. */
  bgColour1: RGB;
  bgColour2: RGB;
  letterColour1: RGB;
  letterColour2: RGB;
}

export interface EFrame {
  show: boolean;
  orientation: Orientation;
  strokePx: number;
  /** Grey codes; used when the RGB codes below are absent. */
  backgroundCode: number;
  letterCode: number;
  backgroundRgb?: RGB | null;
  letterRgb?: RGB | null;
  /** Dither amplitude in output LSBs; 0 = off. */
  dither: number;
  /** Optional centre patch (calibration): code and size in px; replaces the E. */
  patch?: { code: number; sizePx: number } | null;
  jitter?: EdgeJitterFrame | null;
}

const ORIENT: Record<Orientation, number> = { right: 0, left: 1, up: 2, down: 3 };

const FS = `#version 300 es
precision highp float;
precision highp int;
uniform vec2 uOrigin;
uniform float uStroke;
uniform int uOrient;
uniform int uShow;
uniform int uPatch;
uniform vec2 uPatchMin;
uniform vec2 uPatchMax;
uniform float uPatchCode;
uniform vec3 uBg;
uniform vec3 uFg;
uniform float uDither;
uniform float uLsb;
uniform float uFrame;
uniform int uJit;
uniform float uJitInner;
uniform float uJitOuter;
uniform float uJitBlob;
uniform float uJitDensity;
uniform float uJitRate;
uniform vec3 uJitBg1;
uniform vec3 uJitBg2;
uniform vec3 uJitFg1;
uniform vec3 uJitFg2;
uniform vec4 uRect[4];   // the E's strokes (min.xy, max.xy) in device px
uniform vec4 uGap[2];    // the two gaps between the bars
uniform vec4 uBox;       // the E's bounding square
out vec4 outColor;
${GLSL_COMMON}
float boxDist(vec2 p, vec4 r) {
  vec2 q = max(max(r.xy - p, p - r.zw), 0.0);
  return length(q);
}
bool insideE(vec2 p) {
  for (int i = 0; i < 4; i++) {
    if (all(greaterThanEqual(p, uRect[i].xy)) && all(lessThan(p, uRect[i].zw))) return true;
  }
  return false;
}
void main() {
  vec3 c = uBg;
  vec2 px = floor(gl_FragCoord.xy);
  if (uPatch == 1) {
    if (all(greaterThanEqual(px, uPatchMin)) && all(lessThan(px, uPatchMax))) c = vec3(uPatchCode);
  } else if (uShow == 1) {
    vec2 p = px + 0.5;
    bool inE = insideE(p);
    if (inE) c = uFg;
    if (uJit == 1) {
      float d;
      if (inE) {
        d = min(min(p.x - uBox.x, uBox.z - p.x), min(p.y - uBox.y, uBox.w - p.y));
        d = min(d, min(boxDist(p, uGap[0]), boxDist(p, uGap[1])));
      } else {
        d = min(min(boxDist(p, uRect[0]), boxDist(p, uRect[1])), min(boxDist(p, uRect[2]), boxDist(p, uRect[3])));
      }
      if (d <= (inE ? uJitInner : uJitOuter)) {
        vec2 blob = floor((px - uOrigin) / max(1.0, uJitBlob));
        float t = floor(uFrame / max(1.0, uJitRate));
        if (vtHash(blob + vec2(t * 137.7, 0.0)) <= uJitDensity) {
          bool first = vtHash(blob + vec2(0.0, t * 251.3)) < 0.5;
          c = inE ? (first ? uJitFg1 : uJitFg2) : (first ? uJitBg1 : uJitBg2);
        }
      }
    }
  }
  float n = vtDither(0.0, px, uFrame, uDither, uLsb);
  outColor = vec4(c + n, 1.0);
}`;

const NAMES = ["uOrigin", "uStroke", "uOrient", "uShow", "uPatch", "uPatchMin", "uPatchMax",
  "uPatchCode", "uBg", "uFg", "uDither", "uLsb", "uFrame", "uJit", "uJitInner", "uJitOuter",
  "uJitBlob", "uJitDensity", "uJitRate", "uJitBg1", "uJitBg2", "uJitFg1", "uJitFg2", "uRect",
  "uGap", "uBox"];

/** Bottom-left corner of the E in device pixels (integer), centred on the buffer. */
export function letterOrigin(stage: GLStage, strokePx: number): [number, number] {
  const letter = strokePx * 5;
  return [Math.floor(stage.width / 2) - Math.floor(letter / 2), Math.floor(stage.height / 2) - Math.floor(letter / 2)];
}

/** The E's strokes and gaps in cell units (0..5, y = 0 at the bottom), per orientation. */
export function eRects(orientation: Orientation): { strokes: number[][]; gaps: number[][] } {
  switch (orientation) {
    case "right": return { strokes: [[0, 0, 1, 5], [0, 0, 5, 1], [0, 2, 5, 3], [0, 4, 5, 5]], gaps: [[1, 1, 5, 2], [1, 3, 5, 4]] };
    case "left": return { strokes: [[4, 0, 5, 5], [0, 0, 5, 1], [0, 2, 5, 3], [0, 4, 5, 5]], gaps: [[0, 1, 4, 2], [0, 3, 4, 4]] };
    case "up": return { strokes: [[0, 0, 5, 1], [0, 0, 1, 5], [2, 0, 3, 5], [4, 0, 5, 5]], gaps: [[1, 1, 2, 5], [3, 1, 4, 5]] };
    default: return { strokes: [[0, 4, 5, 5], [0, 0, 1, 5], [2, 0, 3, 5], [4, 0, 5, 5]], gaps: [[1, 0, 2, 4], [3, 0, 4, 4]] };
  }
}

export const tumblingE: Stimulus<EFrame> = {
  name: "tumbling-e",
  draw(stage: GLStage, f: EFrame, ctx: DrawContext): void {
    const gl = stage.gl;
    const prog = stage.program("tumbling-e", FS);
    const u = uniformCache.get(prog) ?? uniforms(gl, prog, NAMES);
    uniformCache.set(prog, u);
    gl.useProgram(prog);
    const stroke = Math.max(1, Math.round(f.strokePx));
    const [ox, oy] = letterOrigin(stage, stroke);
    gl.uniform2f(u.uOrigin!, ox, oy);
    gl.uniform1f(u.uStroke!, stroke);
    gl.uniform1i(u.uOrient!, ORIENT[f.orientation]);
    gl.uniform1i(u.uShow!, f.show ? 1 : 0);
    const toPx = (r: number[]) => [ox + r[0]! * stroke, oy + r[1]! * stroke, ox + r[2]! * stroke, oy + r[3]! * stroke];
    const { strokes, gaps } = eRects(f.orientation);
    gl.uniform4fv(u.uRect!, new Float32Array(strokes.flatMap(toPx)));
    gl.uniform4fv(u.uGap!, new Float32Array(gaps.flatMap(toPx)));
    gl.uniform4f(u.uBox!, ox, oy, ox + 5 * stroke, oy + 5 * stroke);
    const patch = f.patch ?? null;
    gl.uniform1i(u.uPatch!, patch ? 1 : 0);
    if (patch) {
      const s = Math.round(patch.sizePx);
      const x0 = Math.floor(stage.width / 2 - s / 2);
      const y0 = Math.floor(stage.height / 2 - s / 2);
      gl.uniform2f(u.uPatchMin!, x0, y0);
      gl.uniform2f(u.uPatchMax!, x0 + s, y0 + s);
      gl.uniform1f(u.uPatchCode!, patch.code);
    }
    const bg = f.backgroundRgb ?? [f.backgroundCode, f.backgroundCode, f.backgroundCode];
    const fg = f.letterRgb ?? [f.letterCode, f.letterCode, f.letterCode];
    gl.uniform3f(u.uBg!, bg[0], bg[1], bg[2]);
    gl.uniform3f(u.uFg!, fg[0], fg[1], fg[2]);
    const j = f.jitter ?? null;
    gl.uniform1i(u.uJit!, j ? 1 : 0);
    if (j) {
      gl.uniform1f(u.uJitInner!, j.innerPx);
      gl.uniform1f(u.uJitOuter!, j.outerPx);
      gl.uniform1f(u.uJitBlob!, j.blobPx);
      gl.uniform1f(u.uJitDensity!, j.density);
      gl.uniform1f(u.uJitRate!, j.updateFrames);
      gl.uniform3f(u.uJitBg1!, ...j.bgColour1);
      gl.uniform3f(u.uJitBg2!, ...j.bgColour2);
      gl.uniform3f(u.uJitFg1!, ...j.letterColour1);
      gl.uniform3f(u.uJitFg2!, ...j.letterColour2);
    }
    gl.uniform1f(u.uDither!, f.dither);
    gl.uniform1f(u.uLsb!, 1 / (2 ** ctx.outputBits - 1));
    gl.uniform1f(u.uFrame!, ctx.frameIndex);
    stage.drawFullscreen(prog);
  },
};

const uniformCache = new WeakMap<WebGLProgram, Record<string, WebGLUniformLocation | null>>();

/** Cell layout of the E, for tests: true where the letter is drawn. y = 0 is the bottom row. */
export function eMask(orientation: Orientation): boolean[][] {
  const rows: boolean[][] = [];
  for (let iy = 0; iy < 5; iy++) {
    const row: boolean[] = [];
    for (let ix = 0; ix < 5; ix++) {
      let spine: boolean;
      let bars: boolean;
      if (orientation === "right") { spine = ix === 0; bars = iy % 2 === 0; }
      else if (orientation === "left") { spine = ix === 4; bars = iy % 2 === 0; }
      else if (orientation === "up") { spine = iy === 0; bars = ix % 2 === 0; }
      else { spine = iy === 4; bars = ix % 2 === 0; }
      row.push(spine || bars);
    }
    rows.push(row);
  }
  return rows;
}
